<template>
  <employee-profile>
    <modal-dialog-prevent-leave
      v-bind:is-shown="isShownDialogLeaveConfirm"
      v-on:back="isShownDialogLeaveConfirm = false"
      v-on:leave="leaveWithoutSave"
      v-on:save="saveAndLeave" />
    <form
      class="employee-admission"
      v-on:submit.prevent="onSubmitEmployeeEdit"
    >

      <template v-if="!isDismissed">
        <input-checkbox
          v-if="!isAdmin"
          title="Выводить в расписании"
          v-model="employeeAdmission.isEnabledWorkingScheduling"
          v-on:input="checkChanged"
        />
        <input-checkbox
          v-if="!isAdmin && employeeAdmission.isEnabledWorkingScheduling"
          title="Запретить ставить сотрудника в другие кабинеты в расписании"
          v-model="employeeAdmission.isForbidSelectOtherCabinetsInSchedule"
          v-on:input="checkChanged"
        />
      </template>
      <div
        class="employee-admission__contact"
        v-if="!isAdmin && employeeAdmission.isEnabledWorkingScheduling"
      >
        <div class="employee-admission__contact__row">
          <multiline-select
            v-bind:items="cabinetOptionList"
            v-bind:label="'Выберите кресло'"
            v-bind:hint="'Кресло по умолчанию нужно для удобства заполнения расписания. В графике работы вы можете в любой день указывать сотруднику другие кресла'"
            v-model="employeeAdmission.cabinetIds"
            v-on:input="checkChanged"
            class="employee-admission__multiline explanation" />
        </div>
        <p
          class="employee-admission__error-text"
          role="alert"
          v-bind:key="id"
          v-for="(text, id) in cabinetInputErrors"
        >{{ text }}</p>
      </div>
      <div
        v-if="!isAdmin && employeeAdmission.isEnabledWorkingScheduling"
      >
        <label class="employee-admission__step">
          <span class="employee-admission__step__label">Шаг приёма</span>
          <select
            class="employee-admission__step__input"
            id="duration-appointment"
            v-model="employeeAdmission.timeStepMinutes"
            v-on:input="checkChanged"
          >
            <option
              v-for="(option, index) in timeStepOptionList"
              v-bind:key="index"
              v-bind:value="option.value"
            >{{ option.title }}</option>
          </select>
        </label>
      </div>
      <div v-if="employeeAdmission.isEnabledWorkingScheduling">
        <label
          class="employee-admission__group"
          v-bind:class="{ field_error: infoInputErrors.length > 0 }"
        >
          <span class="employee-admission__label">Информация о сотруднике</span>
          <textarea
            class="employee-admission__textarea"
            placeholder="Например, записывать только пациентов старше 18 лет"
            v-model="employeeAdmission.info"
            v-on:input="checkChanged"
            maxlength="100"
            rows="2"
            v-letter-capital
          />
          <p
            class="field__error-text"
            role="alert"
            v-bind:key="id"
            v-for="(text, id) in infoInputErrors"
          >{{ text }}</p>
        </label>
      </div>
      <template v-if="!isDismissed">
        <div class="employee-admission__contact__row">
          <b>Уведомлять о начале приема</b>
          <button
            class="employee-admission__button-remove"
            type="button"
            v-if="employeeAdmission.notifyChannel"
            v-on:click="employeeAdmission.notifyChannel = '';"
          >Сбросить</button>
        </div>
        <div
          v-for="({
            title,
            value,
          }, index) in $options.notificationChannels"
          v-bind:key="`radio-key-${index}`"
        >
          <label class="employee-admission__radio">
            <input
              type="radio"
              v-bind:id="`radio-${value}`"
              name="radio-channel-notification"
              v-bind:value="value"
              v-model="employeeAdmission.notifyChannel">
            <span>{{ title }}</span>
          </label>
        </div>
      </template>
      <button
        class="employee-admission__button-save"
        type="submit"
        v-bind:disabled="isAwaitRequest"
      >
        Сохранить
      </button>
    </form>
  </employee-profile>
</template>

<script>
import InputCheckbox from '@/components/InputCheckbox.vue';
import MultilineSelect from '@/components/MultilineSelect.vue';
import EmployeeProfile from '@/views/ClinicPanel/EmployeeProfile.vue';
import { EmployeeAdmissionEditRequest } from '@/storage/gateways/EmployeeGateway';
import notifierMixin from '@/mixins/notifierMixin';
import ModalDialogPreventLeave from '@/components/ModalDialogPreventLeave.vue';
import preventLeaveWithoutConfirmMixin from '@/mixins/preventLeaveWithoutConfirmMixin';
import debounce from 'lodash.debounce';

/**
 * @description Разрешить получение списка ошибок для поля ввода.
 * @param {string} cause Имя поля ввода.
 * @returns {function}
 */
const resolveInputErrors = (cause) => (vm) => (
  cause in vm.profileInputErrors ? vm.profileInputErrors[cause] : []
);

/**
 * Каналы уведомления.
 * @type {[{title: string, value: string},{title: string, value: string}]}
 */
const NOTIFICATION_CHANNEL = [
  {
    title: 'WhatsApp',
    value: 'whatsApp',
  },
  {
    title: 'СМС',
    value: 'sms',
  },
];

/**
 * @description Данные приема сотрудника
 * @param {array}   cabinetIds
 * @param {string}  info
 * @param {number}  timeStepMinutes
 * @param {boolean} isEnabledWorkingScheduling
 * @param {boolean} isForbidSelectOtherCabinetsInSchedule
 * @param {string}  notifyChannel
 * @constructor
 */
function EmployeeAdmission(
  cabinetIds = [],
  info = '',
  timeStepMinutes = 30,
  isEnabledWorkingScheduling = true,
  isForbidSelectOtherCabinetsInSchedule = false,
  notifyChannel = '',
) {
  this.cabinetIds = cabinetIds;
  this.info = info;
  this.timeStepMinutes = timeStepMinutes;
  this.isEnabledWorkingScheduling = isEnabledWorkingScheduling;
  this.isForbidSelectOtherCabinetsInSchedule = isForbidSelectOtherCabinetsInSchedule;
  this.notifyChannel = notifyChannel;

  this.toString = () => JSON.stringify({
    cabinetIds: this.cabinetIds,
    info: this.info,
    timeStepMinutes: this.timeStepMinutes,
    isEnabledWorkingScheduling: this.isEnabledWorkingScheduling,
    isForbidSelectOtherCabinetsInSchedule: this.isForbidSelectOtherCabinetsInSchedule,
    notifyChannel: this.notifyChannel,
  });

  let initial = this.toString();
  this.isChanged = () => this.toString() !== initial;

  /**
   * @description Обновить.
   * @returns {void}
   */
  this.refresh = () => {
    initial = toString();
  };
}

/**
 * @description Компонент пункта профиля сотрудника "Основное".
 * @type {Vue}
 */
export default {
  notificationChannels: NOTIFICATION_CHANNEL,
  computed: {
    needPreventLeave() {
      return this.isChanged;
    },
    employeeId: (vm) => parseInt(vm.$route.params.id, 10),
    /**
     * @description Является ли сотрудник администратором.
     * @return {boolean}
     */
    isAdmin() {
      return this.roleId === 'admin';
    },
    cabinetInputErrors: resolveInputErrors('cabinet-id'),
    infoInputErrors: resolveInputErrors('info'),
  },
  tooltipText: 'Кабинет по умолчанию нужен для удобства заполнения расписания. В графике работы вы можете в любой день указывать сотруднику другие кабинеты.',
  components: {
    ModalDialogPreventLeave,
    EmployeeProfile,
    MultilineSelect,
    InputCheckbox,
  },
  created() {
    this.fetchEmployeeAdmission();
    this.fetchProfileOptions();
  },
  data() {
    return {
      employeeAdmission: new EmployeeAdmission(),
      cabinetOptionList: [],
      timeStepOptionList: [],
      isAwaitRequest: false,
      profileInputErrors: {},
      isDismissed: false,
      isChanged: false,
    };
  },
  methods: {
    /**
     * @description Получить данные по приему сотрудника
     * @returns {void}
     */
    fetchEmployeeAdmission() {
      const onFail = (error) => {
        this.notifyFail(error.title);
      };
      const onSuccess = async (employee) => {
        this.profileInputErrors = {};
        this.employeeAdmission = new EmployeeAdmission(
          employee.cabinetIds,
          employee.info,
          employee.timeStepMinutes,
          employee.isEnabledWorkingScheduling,
          employee.isForbidSelectOtherCabinetsInSchedule,
          employee.notifyChannel,
        );
        this.isDismissed = employee.isDismissed;
      };
      const response = this.$employeeGateway.getById(this.employeeId);
      response.then((result) => result.fold(onFail, onSuccess));
    },
    /**
     * @description Получить опции по профилю.
     * @returns {void}
     */
    fetchProfileOptions() {
      const onResponse = (result) => {
        this.cabinetOptionList = result
          .cabinetList
          .map(({ name: title, id: value }) => ({ title, value }));
        this.timeStepOptionList = result
          .timeStepList
          .map(({ title, number_minutes: value }) => ({ title, value }));
      };
      this.$suggestionGateway.getEmployeeCreation().then(onResponse);
    },
    /**
     * @description Отправить событие отправки редактирования сотрудника.
     * @returns {Promise}
     */
    onSubmitEmployeeEdit() {
      const request = this.makeEmployeeAdmissionRequest();
      this.isAwaitRequest = true;
      const onFail = (error) => {
        this.profileInputErrors = error.allCauses();
        if (!error.hasCauses()) {
          this.notifyFail(error.getTitle());
        }
        this.isAwaitRequest = false;
      };
      const onSuccess = (result) => {
        this.notifySuccess(result.title);
        this.profileInputErrors = {};
        this.isAwaitRequest = false;
        this.isChanged = false;
        this.employeeAdmission.refresh();
      };
      const response = this.$employeeGateway.editAdmission(request);
      response.then((result) => result.fold(onFail, onSuccess));
      return response;
    },
    /**
     * @description Создать запрос на редактирование.
     * @returns {EmployeeAdmissionEditRequest}
     */
    makeEmployeeAdmissionRequest() {
      return new EmployeeAdmissionEditRequest(
        this.employeeId,
        this.employeeAdmission.cabinetIds,
        this.employeeAdmission.info,
        this.employeeAdmission.isEnabledWorkingScheduling,
        this.employeeAdmission.isForbidSelectOtherCabinetsInSchedule,
        this.employeeAdmission.timeStepMinutes,
        this.employeeAdmission.notifyChannel,
      );
    },
    async saveAndLeave() {
      const result = await this.onSubmitEmployeeEdit();
      result.forEach((message) => {
        this.notifySuccess(message.title);
        this.actionAfterClosingDialogLeaveConfirm();
      });
      this.isShownDialogLeaveConfirm = false;
    },
    /**
     * @description Проверить наличие изменений.
     * @returns {void}
     */
    checkChanged: debounce(function _() {
      this.isChanged = this.employeeAdmission.isChanged();
    }, 300),
  },
  mixins: [
    notifierMixin,
    preventLeaveWithoutConfirmMixin,
  ],
  name: 'ClinicPanelEmployeeProfileAdmission',
};
</script>

<style lang="scss">
@use "@/scss/buttons";
.employee-admission {
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin: 37px 0 0 0;
  height: 100%;
  width: 100%;
  max-width: 700px;
  &__contact {
    display: flex;
    flex-direction: column;
    padding: 17px 0 0 0;
    &__row {
      position: relative;
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: 10px;
    }
  }
  &__multiline {
    flex: 1;
    &.explanation {
      .multiline-select__row:first-child {
        .multiline-select__placeholder {
          cursor: pointer !important;
          pointer-events: auto;
          &:after {
            content: "?";
            font-size: 13px;
            margin: 0 0 0 5px;
          }
        }
      }
    }
  }
  &__error-text {
    color: var(--warning-color);
    font-size: 12px;
    line-height: 12px;
    padding: 0;
    margin: 5px 0 0 0;
    background-color: transparent!important;
  }
  &__step {
    display: grid;
    align-items: center;
    gap: 2px;
    width: fit-content;
    &__label {
      font-size: 12px;
      line-height: 16px;
      color: #90A0B7;
    }
    &__input {
      background-color: #ffffff;
      background-image: url("@/assets/img/checked/checked-grey.svg");
      background-position: right 17px center;
      border: 1px solid #c2cfe0;
      border-radius: 4px;
      outline: none;
      height: 40px;
      min-width: 200px;
      padding: 0 10px;
      appearance: none;
      font-size: 16px;
      color: #192a3e;
      font-weight: 400;
      &.validate {
        border-color: var(--warning-color);
      }
      &:focus {
        color: #192a3e;
        background-color: #fff;
        border: 2px solid #0489e0;
        outline: 0;
        background-image: url("@/assets/img/checked/checked-grey-top.svg");
      }
      &:disabled {
        background-color: #fff;
        background-image: url("@/assets/img/checked/checked-grey.svg");
      }
    }
  }
  &__group {
    display: flex;
    flex-direction: column;
    &.field_error {
      .employee-creation__label {
        color: var(--warning-color);
      }
    }
  }
  &__label {
    color: #90A0B7;
    opacity: 0.7;
    font-weight: 400;
    font-size: 12px;
    line-height: 16px;
  }
  &__textarea {
    background: #ffffff;
    border: 1px solid #c2cfe0;
    border-radius: 4px;
    z-index: 2;
    outline: none;
    -webkit-appearance: none;
    width: 100%;
    padding: 10px;
    appearance: none;
    font-size: 16px;
    color: #626262;
    font-weight: 400;
    margin: 0 0 2px 0;
    &:focus {
      background-color: #fff;
      border: 2px solid #0489e0;
      margin: 0;
    }
    &::placeholder {
      color: #a3a3a3;
    }
  }
  &__button-save {
    @include buttons.btn-blue(100%, 48px, 16px);
    min-height: 48px;
    max-width: 174px;
    padding: 0;
    margin: 10px 0 0 0;
    &:disabled {
      background-color: var(--unfocus-color);
    }
  }
  &__button-remove {
    text-align: left;
    color: #0489e0;
    font-weight: 400;
    font-size: 16px;
    line-height: 20px;
  }
  &__radio > input {
    position: absolute;
    z-index: -1;
    opacity: 0;
  }
  &__radio > span {
    display: inline-flex;
    align-items: center;
    user-select: none;
    font-weight: 400;
    font-size: 16px;
    line-height: 19px;
    cursor: pointer;
  }
  &__radio > span::before {
    content: "";
    display: inline-block;
    width: 24px;
    height: 24px;
    flex-shrink: 0;
    flex-grow: 0;
    border: 1px solid #90a0b7;
    border-radius: 50%;
    margin-right: 10px;
    background-repeat: no-repeat;
    background-position: center center;
    background-size: 50% 50%;
  }
  &__radio > input:not(:disabled):not(:checked) + span:hover::before {
    border: 1px solid #0489e0;
  }
  &__radio > input:checked + span::before {
    border: 1px solid #0489e0;
    background-color: #0489e0;
    box-shadow: inset 0 0 0 6px #fff;
  }
}
@media screen and (max-width: 480px) {
  .employee-admission {
    &__step {
      width: 100%;
      &__input {
        width: 100%;
      }
    }
  }
}
</style>
